'use client'

import { createContext, useContext, useState, type ReactNode } from 'react'

import type { ViewMode } from '@/app/components/atoms/VistaToggle'

interface ViewModeContextValue {
  /** Disposición de card seleccionada (horizontal/lista vs. vertical/cuadrícula). */
  viewMode: ViewMode
  setViewMode: (viewMode: ViewMode) => void
}

// `null` por defecto: usar el hook fuera del provider es un error de
// programación y debe fallar de forma explícita, no devolver un valor silencioso.
const ViewModeContext = createContext<ViewModeContextValue | null>(null)

interface ViewModeProviderProps {
  children: ReactNode
  /** Disposición inicial. Por defecto `vertical` (cuadrícula). */
  defaultViewMode?: ViewMode
}

/**
 * `ViewModeProvider` — comparte la disposición de card elegida (horizontal
 * vs. vertical) entre el selector de vista (`VistaToggle`, dentro de
 * `ListingFilters`, junto al botón "Ordenar y filtrar") y el listado
 * virtualizado que lo consume (`AgendaResults`/`LoMasVendidoResults`/
 * `PostsaleResults`) — ambos son descendientes de la misma página (Server
 * Component) pero no son ni padre-hijo ni hermanos directos, así que el
 * estado no puede vivir en ninguno de los dos por separado
 * (specs/019-estados-compra-eventos). Sin persistencia (cookie/URL): es una
 * preferencia de visualización efímera de la sesión de navegación actual.
 */
export function ViewModeProvider({ children, defaultViewMode = 'vertical' }: ViewModeProviderProps) {
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode)

  return <ViewModeContext.Provider value={{ viewMode, setViewMode }}>{children}</ViewModeContext.Provider>
}

export function useViewModeContext(): ViewModeContextValue {
  const ctx = useContext(ViewModeContext)
  if (!ctx) {
    throw new Error('useViewModeContext debe usarse dentro de <ViewModeProvider>.')
  }
  return ctx
}
